import React, { useEffect, useMemo, useState } from "react";
import "../styles/shared-ui.css";
import { useAppContext } from "../context/AppContext";
import { useLocation, useParams } from "react-router-dom";
import { getCompanyNames } from "../services/companyService";
import { getCountries } from "../services/countryService";
import { getCitiesByCountryId } from "../services/cityService";
import { getBuildingTypes } from "../services/spaceCatalogService";
import {
  getBuildingById,
  getBuildingWithRelations,
  createBuilding,
  patchBuilding,
  deleteBuilding,
} from "../services/buildingService";
import { toast } from "../utils/toast";
import ConfirmModal from "../components/ui/ConfirmModal";

const MAX_ROWS = 5;
const MAX_COLS = 10;

const clampInt = (val, min, max) => {
  const n = Number(val);
  if (!Number.isFinite(n)) return min;
  const i = Math.trunc(n);
  return Math.max(min, Math.min(max, i));
};

const toPositiveId = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const emptyForm = {
  building_id: "", // internal numeric id (hidden in UI)
  building_code: "", // shown in UI (string)
  name: "",
  company_id: "",
  company_name: "",
  building_type_id: "",
  countryId: "",
  country_name: "",
  cityId: "",
  city_name: "",
  rows: 0,
  columns: 0,
  floors: "",
  stairs_enabled: false,
  stairs_cell: "",
  elevator_enabled: false,
  elevator_cell: "",
};

const BuildingDetailPage = () => {
  const {
    setActiveSection,
    buildingId,
    setBuildingId,
    buildingFormMode,
    setBuildingFormMode,
  } = useAppContext();
  const location = useLocation();
  const { buildingId: buildingIdParam } = useParams();
  const pathBuildingId = useMemo(() => {
    const m = String(location.pathname || "").match(
      /\/buildings\/(\d+)(?:\/edit)?$/,
    );
    return m ? toPositiveId(m[1]) : null;
  }, [location.pathname]);
  const routeBuildingId = useMemo(() => {
    return (
      toPositiveId(decodeURIComponent(buildingIdParam || "")) ?? pathBuildingId
    );
  }, [buildingIdParam, pathBuildingId]);

  const effectiveBuildingId = useMemo(() => {
    if (routeBuildingId != null) return routeBuildingId;
    if (buildingId != null) return buildingId;
    try {
      const storedId = localStorage.getItem("lastBuildingId");
      const storedNumeric = storedId ? Number(storedId) : null;
      return Number.isFinite(storedNumeric) && storedNumeric > 0 ?
          storedNumeric
        : null;
    } catch {
      return null;
    }
  }, [routeBuildingId, buildingId]);

  useEffect(() => {
    if (
      routeBuildingId != null &&
      routeBuildingId > 0 &&
      buildingId !== routeBuildingId
    ) {
      setBuildingId(routeBuildingId);
    }
  }, [routeBuildingId, buildingId, setBuildingId]);

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [buildingTypes, setBuildingTypes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [floors, setFloors] = useState([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const mode = buildingFormMode || "view";
  const isCreate = mode === "create";
  const isEdit = mode === "edit";
  const isView = mode === "view";
  const isDelete = mode === "delete";
  const isReadOnly = isView || isDelete;

  const totalCells = useMemo(
    () => (Number(form.rows) || 0) * (Number(form.columns) || 0),
    [form.rows, form.columns],
  );

  // Persist current buildingId (if present)
  useEffect(() => {
    if (buildingId != null) {
      try {
        localStorage.setItem("lastBuildingId", String(buildingId));
      } catch {}
    }
  }, [buildingId]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const shouldLoadReferenceData = !isView;
        const companiesPromise =
          shouldLoadReferenceData ? getCompanyNames() : Promise.resolve([]);
        const countriesPromise =
          shouldLoadReferenceData ? getCountries() : Promise.resolve([]);
        const buildingTypesPromise = getBuildingTypes().catch(() => []);
        const [companiesData, buildingData, countriesData, buildingTypesData] =
          await Promise.all([
            companiesPromise,
            isCreate ? Promise.resolve(null)
            : isView ?
              effectiveBuildingId != null ?
                getBuildingWithRelations(effectiveBuildingId)
              : Promise.resolve(null)
            : effectiveBuildingId != null ? getBuildingById(effectiveBuildingId)
            : Promise.resolve(null),
            countriesPromise,
            buildingTypesPromise,
          ]);
        if (!mounted) return;
        setCompanies(Array.isArray(companiesData) ? companiesData : []);
        setCountries(Array.isArray(countriesData) ? countriesData : []);
        setBuildingTypes(
          Array.isArray(buildingTypesData) ? buildingTypesData : [],
        );
        if (isCreate) {
          setForm({ ...emptyForm });
          setCities([]);
        } else if (buildingData) {
          // For view mode, 'with-relations' returns cells (row/column, type)
          const rowsVal = Number(buildingData.rows ?? 0) || 0;
          const colsVal = Number(buildingData.columns ?? 0) || 0;
          const cells =
            Array.isArray(buildingData.cells) ? buildingData.cells : [];
          const findCellNum = (type) => {
            const entry = cells.find(
              (c) => String(c.type).toUpperCase() === type,
            );
            if (!entry || !colsVal) return "";
            const r = Number(entry.row),
              c = Number(entry.column);
            if (!Number.isFinite(r) || !Number.isFinite(c) || r < 1 || c < 1)
              return "";
            return (r - 1) * colsVal + c; // 1-based cell index
          };
          const stairsCell =
            isView ? findCellNum("STAIRS") : (buildingData.stairs_cell ?? "");
          const elevatorCell =
            isView ?
              findCellNum("ELEVATOR")
            : (buildingData.elevator_cell ?? "");

          const countryId =
            buildingData.countryId ?? buildingData.country_id ?? "";
          const cityId = buildingData.cityId ?? buildingData.city_id ?? "";

          setForm({
            building_id: buildingData.building_id ?? "",
            building_code: buildingData.building_code ?? "",
            name: buildingData.name ?? "",
            company_id: buildingData.company_id ?? "",
            company_name: buildingData.company?.name ?? "",
            building_type_id: buildingData.building_type_id ?? "",
            countryId: countryId ?? "",
            country_name:
              buildingData.country?.countryName ??
              buildingData.country?.name ??
              buildingData.country ??
              "",
            cityId: cityId ?? "",
            city_name:
              buildingData.city?.cityName ??
              buildingData.city?.name ??
              buildingData.city ??
              "",
            rows: rowsVal,
            columns: colsVal,
            floors: buildingData.floors ?? "",
            stairs_enabled: stairsCell !== "",
            stairs_cell: stairsCell,
            elevator_enabled: elevatorCell !== "",
            elevator_cell: elevatorCell,
          });
          setFloors(
            Array.isArray(buildingData.floors) ? buildingData.floors : [],
          );
        } else {
          toast.error("Building not found");
        }
      } catch (e) {
        if (!mounted) return;
        toast.error(e?.message || "Failed to load data");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [effectiveBuildingId, isCreate, isView]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (isReadOnly) {
        setCities([]);
        setCitiesLoading(false);
        return;
      }
      const countryId = form.countryId;
      if (!countryId) {
        setCities([]);
        return;
      }
      try {
        setCitiesLoading(true);
        const cityList = await getCitiesByCountryId(countryId);
        if (!mounted) return;
        const safeCities = Array.isArray(cityList) ? cityList : [];
        setCities(safeCities);

        const selectedCity = safeCities.find(
          (city) =>
            String(city?.cityId ?? city?.city_id ?? city?.id ?? "") ===
            String(form.cityId ?? ""),
        );
        if (selectedCity) {
          setForm((prev) => ({
            ...prev,
            city_name:
              prev.city_name ||
              selectedCity.cityName ||
              selectedCity.city_code ||
              selectedCity.name ||
              "",
            country_name:
              prev.country_name ||
              selectedCity.country?.countryName ||
              selectedCity.country?.name ||
              selectedCity.countryName ||
              "",
          }));
        }
      } catch {
        if (mounted) setCities([]);
      } finally {
        if (mounted) setCitiesLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [form.countryId, form.cityId, isReadOnly]);

  const title = useMemo(() => {
    if (isCreate) return "Create Building";
    if (isEdit) return "Edit Building";
    if (isDelete) return "Delete Building";
    return "View Building";
  }, [isCreate, isEdit, isDelete]);

  const currentBuildingId = useMemo(() => {
    if (routeBuildingId != null) return routeBuildingId;
    if (buildingId != null) return buildingId;
    const fallback = Number(form?.building_id);
    return Number.isFinite(fallback) && fallback > 0 ? fallback : null;
  }, [routeBuildingId, buildingId, form.building_id]);

  const backToList = () => {
    setBuildingId(null);
    setBuildingFormMode("view");
    setActiveSection("buildings");
  };

  const validate = () => {
    const errors = [];
    // building_id is internal numeric and hidden; no validation required for UI-only now
    if (!String(form.name || "").trim())
      errors.push("Building name is required");
    if (
      (isCreate || isEdit) &&
      (form.company_id === "" ||
        form.company_id === null ||
        Number.isNaN(Number(form.company_id)))
    )
      errors.push("Company is required");
    if ((isCreate || isEdit) && !Number(form.countryId))
      errors.push("Country is required");
    if ((isCreate || isEdit) && !Number(form.cityId))
      errors.push("City is required");
    const rows = Number(form.rows),
      cols = Number(form.columns);
    if (
      (isCreate || isEdit) &&
      (!Number.isInteger(rows) || rows < 0 || rows > MAX_ROWS)
    )
      errors.push(`Rows must be an integer between 0 and ${MAX_ROWS}`);
    if (
      (isCreate || isEdit) &&
      (!Number.isInteger(cols) || cols < 0 || cols > MAX_COLS)
    )
      errors.push(`Columns must be an integer between 0 and ${MAX_COLS}`);

    const maxCell = rows * cols;
    const checkCell = (value, label) => {
      if (value === "" || value === null || value === undefined) return;
      const n = Number(value);
      if (!Number.isInteger(n) || n < 1 || n > maxCell)
        errors.push(`${label} must be between 1 and ${maxCell}`);
    };

    if (form.stairs_enabled) checkCell(form.stairs_cell, "Stairs cell");
    if (form.elevator_enabled) checkCell(form.elevator_cell, "Elevator cell");

    // Ensure stairs and elevator are not on same cell and at least one is selected
    if (isEdit || isCreate) {
      const hasStairs =
        form.stairs_enabled &&
        Number.isInteger(Number(form.stairs_cell)) &&
        Number(form.stairs_cell) >= 1 &&
        Number(form.stairs_cell) <= maxCell;
      const hasElevator =
        form.elevator_enabled &&
        Number.isInteger(Number(form.elevator_cell)) &&
        Number(form.elevator_cell) >= 1 &&
        Number(form.elevator_cell) <= maxCell;
      // At least one of stairs or elevator must be enabled with a valid cell
      if (!hasStairs && !hasElevator) {
        errors.push(
          "Select at least one: stairs or elevator with a valid cell",
        );
      }
      if (
        hasStairs &&
        hasElevator &&
        Number(form.stairs_cell) === Number(form.elevator_cell)
      ) {
        errors.push("Stairs and elevator cannot be on the same cell");
      }
    }
    return errors;
  };

  const handleCreate = async () => {
    const errs = validate();
    if (errs.length) {
      toast.error(errs[0]);
      return;
    }
    try {
      const rows = clampInt(form.rows, 0, MAX_ROWS);
      const columns = clampInt(form.columns, 0, MAX_COLS);
      const maxCell = rows * columns;

      // Build layout array from enabled features
      const layout = [];
      const toRowCol = (cell) => {
        const idx = Math.min(Math.max(1, Number(cell)), maxCell);
        const zero = idx - 1;
        return {
          row: Math.floor(zero / (columns || 1)) + 1,
          column: (zero % (columns || 1)) + 1,
        };
      };
      if (
        form.stairs_enabled &&
        form.stairs_cell !== "" &&
        Number.isFinite(Number(form.stairs_cell))
      ) {
        const { row, column } = toRowCol(form.stairs_cell);
        layout.push({ row, column, type: "STAIRS" });
      }
      if (
        form.elevator_enabled &&
        form.elevator_cell !== "" &&
        Number.isFinite(Number(form.elevator_cell))
      ) {
        const { row, column } = toRowCol(form.elevator_cell);
        layout.push({ row, column, type: "ELEVATOR" });
      }

      await createBuilding({
        building_code: String(form.building_code || "").trim(),
        name: String(form.name).trim(),
        company_id: Number(form.company_id),
        building_type_id:
          form.building_type_id !== "" ? Number(form.building_type_id) : null,
        countryId: Number(form.countryId),
        cityId: Number(form.cityId),
        rows,
        columns,
        ...(layout.length > 0 ? { layout } : {}),
      });
      toast.success(`Building "${form.name}" has been created successfully!`);
      backToList();
    } catch (e) {
      toast.error(e?.message || "Failed to create building");
    }
  };

  const handleSave = async () => {
    const errs = validate();
    if (errs.length) {
      toast.error(errs[0]);
      return;
    }
    try {
      const rows = Number(form.rows) || 0;
      const columns = Number(form.columns) || 0;
      const layout = [];
      const toRowCol = (idx) => ({
        row: Math.floor(idx / (columns || 1)) + 1,
        column: (idx % (columns || 1)) + 1,
      });
      if (
        form.stairs_cell !== "" &&
        Number.isFinite(Number(form.stairs_cell))
      ) {
        const { row, column } = toRowCol(Number(form.stairs_cell));
        layout.push({ row, column, type: "STAIRS" });
      }
      if (
        form.elevator_cell !== "" &&
        Number.isFinite(Number(form.elevator_cell))
      ) {
        const { row, column } = toRowCol(Number(form.elevator_cell));
        layout.push({ row, column, type: "ELEVATOR" });
      }

      const storedId = (() => {
        try {
          return localStorage.getItem("lastBuildingId");
        } catch {
          return null;
        }
      })();
      const effectiveId = buildingId ?? (storedId ? Number(storedId) : null);
      await patchBuilding(effectiveId, {
        building_code: String(form.building_code || "").trim(),
        name: String(form.name).trim(),
        company_id: Number(form.company_id),
        building_type_id:
          form.building_type_id !== "" ? Number(form.building_type_id) : null,
        countryId: Number(form.countryId),
        cityId: Number(form.cityId),
        rows: rows,
        columns: columns,
        ...(layout.length > 0 ? { layout } : {}),
      });
      toast.success(`Building "${form.name}" has been updated successfully!`);
      backToList();
    } catch (e) {
      toast.error(e?.message || "Failed to save building");
    }
  };

  const handleDelete = async () => {
    setConfirmDeleteOpen(true);
  };

  const confirmDeleteBuilding = async () => {
    setConfirmDeleteOpen(false);
    try {
      const storedId = (() => {
        try {
          return localStorage.getItem("lastBuildingId");
        } catch {
          return null;
        }
      })();
      const effectiveId = buildingId ?? (storedId ? Number(storedId) : null);
      await deleteBuilding(effectiveId);
      toast.success(`Building has been deleted successfully!`);
      backToList();
    } catch (e) {
      toast.error(e?.message || "Failed to delete building");
    }
  };

  const renderCompanyField = () => {
    if (isView || isDelete) {
      const companyText =
        form.company_name ||
        companies.find((c) => Number(c.company_id) === Number(form.company_id))
          ?.name ||
        "";
      return (
        <div className="field">
          <label>Company</label>
          <input type="text" value={companyText} readOnly disabled />
        </div>
      );
    }
    return (
      <div className="field">
        <label>Company</label>
        <select
          value={form.company_id}
          onChange={(e) =>
            setForm((f) => ({ ...f, company_id: e.target.value }))
          }
        >
          <option value="">Select a company</option>
          {companies.map((c) => (
            <option key={c.company_id} value={c.company_id}>
              {c.company_id} - {c.name}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const renderBuildingTypeField = () => {
    if (isView || isDelete) {
      const typeText =
        buildingTypes.find(
          (t) => Number(t.building_type_id) === Number(form.building_type_id),
        )?.name || "-";
      return (
        <div className="field">
          <label>Building Type</label>
          <input type="text" value={typeText} readOnly disabled />
        </div>
      );
    }
    return (
      <div className="field">
        <label>Building Type</label>
        <select
          value={form.building_type_id}
          onChange={(e) =>
            setForm((f) => ({ ...f, building_type_id: e.target.value }))
          }
        >
          <option value="">Select a building type</option>
          {[...buildingTypes]
            .sort((a, b) => {
              const aSchool = String(a.code).toUpperCase() === "OFFICE";
              const bSchool = String(b.code).toUpperCase() === "OFFICE";
              if (aSchool === bSchool) return 0;
              return aSchool ? -1 : 1;
            })
            .map((t) => {
              const isSchool = String(t.code).toUpperCase() === "OFFICE";
              return (
                <option
                  key={t.building_type_id}
                  value={t.building_type_id}
                  disabled={!isSchool}
                  style={!isSchool ? { color: "#b0b0b0" } : undefined}
                >
                  {t.name}
                </option>
              );
            })}
        </select>
      </div>
    );
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">{title}</h2>
            <p className="page-subtitle">
              {isCreate ?
                "Create a new building"
              : isEdit ?
                "Edit building details"
              : isDelete ?
                "Confirm deletion of this building"
              : "View building details"}
            </p>
          </div>
          <div className="roles-toolbar">
            <button className="secondary-btn" onClick={backToList}>
              Back to Buildings
            </button>
            {isView && (
              <>
                <button
                  className="primary-btn"
                  onClick={() => {
                    if (currentBuildingId != null) {
                      setBuildingFormMode("edit");
                      setBuildingId(currentBuildingId);
                      setActiveSection("building-edit", {
                        buildingId: currentBuildingId,
                      });
                    }
                  }}
                >
                  Edit
                </button>
                <button
                  className="danger-btn"
                  onClick={() => {
                    setBuildingFormMode("delete"); /* keep id */
                  }}
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="page-content">
        {loading ?
          <div className="no-results">Loading...</div>
        : <>
            <div className="role-card create-card">
              <div className="field">
                <label>Building Code</label>
                <input
                  type="text"
                  value={form.building_code}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, building_code: e.target.value }))
                  }
                  disabled={isReadOnly}
                />
              </div>
              <div className="field">
                <label>Building Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, name: e.target.value }))
                  }
                  disabled={isReadOnly}
                />
              </div>
              {renderCompanyField()}
              {renderBuildingTypeField()}
              <>
                <div className="field">
                  <label>Country</label>
                  <select
                    value={form.countryId}
                    onChange={(e) => {
                      const next = e.target.value;
                      setForm((f) => ({
                        ...f,
                        countryId: next,
                        country_name: "",
                        cityId: "",
                        city_name: "",
                      }));
                    }}
                    disabled={isReadOnly}
                  >
                    <option value="">
                      {isReadOnly ? "-" : "Select a country"}
                    </option>
                    {(isReadOnly ?
                      [
                        {
                          id: form.countryId,
                          countryName: form.country_name,
                        },
                      ]
                    : countries
                    )
                      .filter(
                        (c) =>
                          (c?.id ?? c?.countryId ?? c?.country_id ?? "") !== "",
                      )
                      .map((c) => {
                        const id = c.countryId ?? c.country_id ?? c.id;
                        const label = c.countryName || c.name || "-";
                        return (
                          <option key={id} value={id}>
                            {label}
                          </option>
                        );
                      })}
                  </select>
                </div>
                <div className="field">
                  <label>City</label>
                  <select
                    value={form.cityId}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        cityId: e.target.value,
                        city_name:
                          cities.find(
                            (city) =>
                              String(
                                city.cityId ?? city.city_id ?? city.id ?? "",
                              ) === String(e.target.value),
                          )?.cityName ||
                          cities.find(
                            (city) =>
                              String(
                                city.cityId ?? city.city_id ?? city.id ?? "",
                              ) === String(e.target.value),
                          )?.name ||
                          "",
                      }))
                    }
                    disabled={isReadOnly || !form.countryId || citiesLoading}
                  >
                    <option value="">
                      {form.countryId ?
                        citiesLoading ?
                          "Loading cities..."
                        : "Select a city"
                      : "Select country first"}
                    </option>
                    {(() => {
                      const list = Array.isArray(cities) ? cities : [];
                      const selectedExists = list.some(
                        (city) =>
                          String(
                            city?.cityId ?? city?.city_id ?? city?.id ?? "",
                          ) === String(form.cityId ?? ""),
                      );
                      if (!isReadOnly || selectedExists || !form.cityId) {
                        return list.map((c) => {
                          const id = c.cityId ?? c.city_id ?? c.id;
                          return (
                            <option key={id} value={id}>
                              {c.cityName || c.name || c.cityCode || "-"}
                            </option>
                          );
                        });
                      }
                      return [
                        <option key={form.cityId} value={form.cityId}>
                          {form.city_name || "-"}
                        </option>,
                      ];
                    })()}
                  </select>
                </div>
              </>
              <div className="field">
                <label>Rows (max {MAX_ROWS})</label>
                <input
                  type="number"
                  min="0"
                  max={MAX_ROWS}
                  value={form.rows}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      rows: clampInt(e.target.value, 0, MAX_ROWS),
                    }))
                  }
                  disabled={isReadOnly}
                />
              </div>
              <div className="field">
                <label>Columns (max {MAX_COLS})</label>
                <input
                  type="number"
                  min="0"
                  max={MAX_COLS}
                  value={form.columns}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      columns: clampInt(e.target.value, 0, MAX_COLS),
                    }))
                  }
                  disabled={isReadOnly}
                />
              </div>
              {(isCreate || isEdit) && (
                <>
                  <div
                    className="divider"
                    style={{ margin: "16px 0", borderTop: "1px solid #e5e7eb" }}
                  />

                  <div className="field">
                    <label>Stairs</label>
                    <div
                      style={{ display: "flex", gap: 12, alignItems: "center" }}
                    >
                      <label
                        style={{
                          display: "inline-flex",
                          gap: 8,
                          alignItems: "center",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={form.stairs_enabled}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              stairs_enabled: e.target.checked,
                            }))
                          }
                        />
                        Enable
                      </label>
                      <input
                        type="number"
                        placeholder="Cell number"
                        min={1}
                        max={Math.max(1, totalCells || 1)}
                        disabled={!form.stairs_enabled || totalCells === 0}
                        value={form.stairs_cell}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === "") {
                            setForm((f) => ({ ...f, stairs_cell: "" }));
                            return;
                          }
                          const clamped = clampInt(
                            v,
                            1,
                            Math.max(1, totalCells || 1),
                          );
                          setForm((f) => ({ ...f, stairs_cell: clamped }));
                        }}
                        style={{ width: 120 }}
                      />
                    </div>
                  </div>

                  <div className="field">
                    <label>Elevator</label>
                    <div
                      style={{ display: "flex", gap: 12, alignItems: "center" }}
                    >
                      <label
                        style={{
                          display: "inline-flex",
                          gap: 8,
                          alignItems: "center",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={form.elevator_enabled}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              elevator_enabled: e.target.checked,
                            }))
                          }
                        />
                        Enable
                      </label>
                      <input
                        type="number"
                        placeholder="Cell number"
                        min={1}
                        max={Math.max(1, totalCells || 1)}
                        disabled={!form.elevator_enabled || totalCells === 0}
                        value={form.elevator_cell}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === "") {
                            setForm((f) => ({ ...f, elevator_cell: "" }));
                            return;
                          }
                          const clamped = clampInt(
                            v,
                            1,
                            Math.max(1, totalCells || 1),
                          );
                          setForm((f) => ({ ...f, elevator_cell: clamped }));
                        }}
                        style={{ width: 120 }}
                      />
                    </div>
                  </div>

                  {/* Visual Grid Preview */}
                  <div style={{ marginTop: 12 }}>
                    {totalCells > 0 && (
                      <div
                        className="grid"
                        style={{
                          display: "grid",
                          gridTemplateColumns: `repeat(${Number(
                            form.columns,
                          )}, minmax(60px, 1fr))`,
                          gap: 8,
                        }}
                      >
                        {Array.from({ length: totalCells }, (_, i) => {
                          const n = i + 1;
                          const isStairs =
                            form.stairs_enabled &&
                            Number(form.stairs_cell) === n;
                          const isElevator =
                            form.elevator_enabled &&
                            Number(form.elevator_cell) === n;
                          return (
                            <div
                              key={n}
                              className={`cell ${isStairs ? "stairs" : ""} ${
                                isElevator ? "elevator" : ""
                              }`}
                            >
                              {n}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}
              {isView && (
                <>
                  <div className="field">
                    <label>Stairs Cell</label>
                    <input
                      type="text"
                      value={form.stairs_cell || "-"}
                      disabled
                    />
                  </div>
                  <div className="field">
                    <label>Elevator Cell</label>
                    <input
                      type="text"
                      value={form.elevator_cell || "-"}
                      disabled
                    />
                  </div>
                  {/* Read-only grid with highlights for stairs/elevator */}
                  <div style={{ marginTop: 12 }}>
                    {(Number(form.rows) || 0) > 0 &&
                      (Number(form.columns) || 0) > 0 && (
                        <div
                          className="grid"
                          style={{
                            display: "grid",
                            gridTemplateColumns: `repeat(${Number(
                              form.columns,
                            )}, minmax(60px, 1fr))`,
                            gap: 8,
                          }}
                        >
                          {Array.from(
                            {
                              length:
                                (Number(form.rows) || 0) *
                                (Number(form.columns) || 0),
                            },
                            (_, i) => {
                              const n = i + 1;
                              const isStairs = Number(form.stairs_cell) === n;
                              const isElevator =
                                Number(form.elevator_cell) === n;
                              return (
                                <div
                                  key={n}
                                  className={`cell ${
                                    isStairs ? "stairs" : ""
                                  } ${isElevator ? "elevator" : ""}`}
                                >
                                  {n}
                                </div>
                              );
                            },
                          )}
                        </div>
                      )}
                  </div>
                </>
              )}
              {isView && (
                <div className="field">
                  <label>Floors</label>
                  {floors.length === 0 ?
                    <div className="no-results" style={{ marginTop: 8 }}>
                      No floors
                    </div>
                  : <div className="roles-table roles-table--fit" style={{ marginTop: 8 }}>
                      <div
                        className="roles-table-header"
                        style={{ gridTemplateColumns: "1fr 1fr" }}
                      >
                        <div className="cell">Floor Code</div>
                        <div className="cell">Name</div>
                      </div>
                      {floors.map((fl) => (
                        <div
                          className="roles-table-row"
                          key={fl.floor_id}
                          style={{ gridTemplateColumns: "1fr 1fr" }}
                        >
                          <div className="cell">{fl.floor_code || "-"}</div>
                          <div className="cell">{fl.name || "-"}</div>
                        </div>
                      ))}
                    </div>
                  }
                </div>
              )}
              <div className="actions">
                {isCreate && (
                  <>
                    <button className="primary-btn" onClick={handleCreate}>
                      Create
                    </button>
                    <button className="secondary-btn" onClick={backToList}>
                      Cancel
                    </button>
                  </>
                )}
                {isEdit && (
                  <>
                    <button className="primary-btn" onClick={handleSave}>
                      Save
                    </button>
                    <button className="secondary-btn" onClick={backToList}>
                      Cancel
                    </button>
                  </>
                )}
                {isDelete && (
                  <>
                    <button className="danger-btn" onClick={handleDelete}>
                      Delete
                    </button>
                    <button className="secondary-btn" onClick={backToList}>
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
          </>
        }
      </div>

      <ConfirmModal
        isOpen={confirmDeleteOpen}
        title="Confirm Delete"
        message={`Are you sure you want to delete this building?`}
        onConfirm={confirmDeleteBuilding}
        onCancel={() => setConfirmDeleteOpen(false)}
      />
    </div>
  );
};

export default BuildingDetailPage;

// Inline styles for the read-only grid in View mode
// Kept local to this page as requested (no global/style file changes)
// Matches the styling used on the Edit page
// Note: Using a fragment to inject a <style> tag adjacent to export is not valid.
// So we attach this style by rendering it once at module load via document head if available.
if (typeof document !== "undefined") {
  const STYLE_ID = "building-detail-view-grid-styles";
  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
      .grid .cell {
        border: 1px solid #cbd5e1;
        border-radius: 10px;
        background: #f8fafc;
        color: #94a3b8;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 60px;
        font-weight: 600;
      }
      .grid .cell.stairs { outline: 2px solid #10b981; color: #0f766e; background: #ecfdf5; }
      .grid .cell.elevator { outline: 2px solid #3b82f6; color: #1d4ed8; background: #eff6ff; }
    `;
    document.head.appendChild(style);
  }
}
